/**
 * Making the counts the install funnel is built on, from the browser (F-123).
 *
 * The DOM half of `funnel.ts`: storage, the network and the platform facts. Every decision
 * about *whether* to count lives next door where it can be tested; everything here is the
 * part that needs a browser.
 *
 * ## Why this exists at all
 *
 * No web app can install itself in one tap on iOS, so some proportion of iOS learners will
 * never install and will therefore never be reachable — push on iOS requires a home-screen
 * app. If that proportion is bad enough the answer is a native app, which is expensive and
 * permanent; if it is fine, building one is a costly mistake. Nobody can tell which from
 * instinct, and this project's market is exactly the one where iOS is likely to be the
 * majority. So: count the funnel, per route, and decide from the numbers.
 *
 * ## What is sent
 *
 * Two enum words, to our own API, from a page the visitor is already on. No identifier is
 * minted, stored or transmitted, no third party is involved, nothing is read from the device
 * that was not needed to draw the screen, and the one thing kept on the device — a list of
 * "already counted" markers — never leaves it. That is the whole of it, and it is the whole
 * of it on purpose: this is first-party counting of a funnel we own, and anything that could
 * follow somebody between visits would be a different thing wearing the same name.
 */
import { countInstallStep } from '../api';
import { COUNTED_KEY, countOnce } from './funnel';
import { installFacts } from './prompt';
import { platformRoute, type FunnelRoute, type FunnelStage } from '@nabu/shared/install';

/** What happened, for the one caller that needs to tell the API about a failure. */
export type CountOutcome =
  /** Counted, and the API accepted it. */
  | 'sent'
  /** Already counted in this browser, or not a pair we may count. Nothing is wrong. */
  | 'skipped'
  /** The request did not land. The counting path is broken and somebody should know. */
  | 'failed';

/**
 * `localStorage` reads and writes that cannot take the app down.
 *
 * Both throw, and not only in exotic cases: Safari in private mode threw on *write* for
 * years, a browser configured to block site data throws on read, and an origin at its
 * storage quota throws on write. Every one of those is a visitor who should still get a
 * working product and simply not be counted, so a failure here degrades to "count it again
 * next time" rather than to a blank screen.
 */
function readMarker(): string | null {
  try {
    return window.localStorage.getItem(COUNTED_KEY);
  } catch {
    return null;
  }
}

function writeMarker(value: string): void {
  try {
    window.localStorage.setItem(COUNTED_KEY, value);
  } catch {
    /*
     * Swallowed, and the consequence is worth stating: this browser will offer to count the
     * same step again on its next visit, which inflates the figure for visitors whose
     * storage is refusing us. They are also the visitors least likely to be able to install
     * anything, so the bias is towards a *worse*-looking install rate rather than a better
     * one — the safe direction for a number that could otherwise talk somebody into building
     * a native app.
     */
  }
}

/**
 * Counts one step, at most once per browser, ever.
 *
 * The marker is written before the request rather than after it; `CountDecision.store` in
 * `funnel.ts` explains which of the two ways of being wrong that chooses and why.
 */
export async function countStep(stage: FunnelStage, route: FunnelRoute): Promise<CountOutcome> {
  const decision = countOnce(readMarker(), stage, route);
  if (!decision.send || !decision.store) return 'skipped';
  writeMarker(decision.store);
  return (await countInstallStep(stage, route)) ? 'sent' : 'failed';
}

/**
 * The only signal there is that an iOS install happened: the app being opened.
 *
 * **WebKit has no `appinstalled` event and no install API at all**, so on iOS an install
 * produces no event, no callback and no network request — the next thing that happens is a
 * different session, possibly days later, arriving in standalone mode. This is that arrival.
 *
 * **Chromium is counted the same way even though it does fire `appinstalled`.** That is
 * deliberate and it is the single most important decision in this feature. The entire
 * purpose of these numbers is to compare iOS against everything else; counting Chromium at
 * the moment of installation and iOS at the moment of first launch would compare two
 * different events and hand the owner a difference that is partly an artefact of how it was
 * measured. One definition, and it is the only one iOS can support: **a first launch from
 * the home screen.** It undercounts on every platform in the same direction — somebody who
 * installs and then keeps using the tab is never counted — which leaves the comparison
 * intact, which is the thing being bought.
 *
 * Called from `main.tsx` before React, because a standalone launch is a page load and the
 * count should not wait on a component tree, a session, or a route the learner might leave
 * before it renders.
 *
 * **Known distortion, stated rather than hidden:** a browser that already had the app
 * installed before F-123 shipped has no marker, so its first launch after the deploy counts
 * as an install that happened earlier. At this volume that is a handful of devices in the
 * first days and nothing afterwards. Read the funnel from a week after the deploy, not from
 * the day of it.
 */
export function countArrival(): void {
  if (typeof window === 'undefined') return;
  const facts = installFacts();
  // A tab is not an arrival. The offer is counted where it is shown, by `Install.tsx`.
  if (!facts.standalone) return;
  void countStep('installed', platformRoute(facts.apple));
}
